import { MaintenancePlanAction } from "@boark/shared/types/maintenance-plan-action";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "../../components/button";
import { Confirm } from "../../components/confirm";
import { H1 } from "../../components/h1";
import { Modal } from "../../components/modal";
import { Select } from "../../components/select";
import {
  useCreateMaintenancePlanAction,
  useDeleteMaintenancePlanAction,
  useGetMaintenancePlanActions,
} from "../../queries/maintenance-plan-action";
import { useGetMaintenancePlan } from "../../queries/maintenance-plan";
import { formatMaintenancePlanCodeLabel } from "../../services/maintenance-plan-group";
import { formatPropertyPartLabel } from "../../services/property-part";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import {
  formatYearMonth,
  getMaintenancePlanSchedule,
} from "../../utils/maintenance-plan-schedule";
import { navigateBack } from "../../utils/navigate-back";
import { formatPrice } from "../../utils/format-price";

const YEAR_RANGE_START = 1990;
const YEAR_RANGE_END_OFFSET = 20;

function getMonthOptions(language: string) {
  return Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    const label = new Intl.DateTimeFormat(language, { month: "long" }).format(
      new Date(2000, index, 1),
    );
    return { label, value: String(month) };
  });
}

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const endYear = currentYear + YEAR_RANGE_END_OFFSET;
  const years: { label: string; value: string }[] = [];
  for (let year = endYear; year >= YEAR_RANGE_START; year -= 1) {
    years.push({ label: String(year), value: String(year) });
  }
  return years;
}

export function MaintenancePlanView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { locale: t, currentLanguage } = useLocale();

  if (!id) {
    throw new Error("Maintenance plan ID is required");
  }

  const [selectedYear, setSelectedYear] = useState(() =>
    String(new Date().getFullYear()),
  );
  const [selectedMonth, setSelectedMonth] = useState(() =>
    String(new Date().getMonth() + 1),
  );

  const { data: maintenancePlan } = useGetMaintenancePlan(id);
  const { data: actions } = useGetMaintenancePlanActions(id);
  const createAction = useCreateMaintenancePlanAction(id);

  const monthOptions = useMemo(
    () => getMonthOptions(currentLanguage),
    [currentLanguage],
  );
  const yearOptions = useMemo(() => getYearOptions(), []);

  const schedule = useMemo(() => {
    if (!maintenancePlan) {
      return undefined;
    }
    return getMaintenancePlanSchedule({
      createdAt: maintenancePlan.createdAt,
      yearlyFrequency: maintenancePlan.yearlyFrequency,
      actions: actions ?? [],
    });
  }, [actions, maintenancePlan]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/maintenance/plan");
  };

  const handleAddAction = async () => {
    await createAction.mutateAsync({
      year: Number(selectedYear),
      month: Number(selectedMonth),
    });
  };

  return (
    <Modal open onClose={closeModal}>
      {maintenancePlan && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <H1>
              {formatMaintenancePlanCodeLabel({
                code: maintenancePlan.maintenancePlanCode.code,
                groupName:
                  maintenancePlan.maintenancePlanCode.maintenancePlanGroup
                    .name,
              })}
            </H1>
            <Field label={t.maintenance.description}>
              {maintenancePlan.description}
            </Field>
            <Field label={t.maintenance.propertyPart}>
              {formatPropertyPartLabel(maintenancePlan.propertyPart)}
            </Field>
            <Field label={t.maintenance.cost}>
              {maintenancePlan.cost !== undefined
                ? formatPrice(maintenancePlan.cost)
                : "-"}
            </Field>
            <Field label={t.maintenance.yearlyFrequency}>
              {maintenancePlan.yearlyFrequency ?? "-"}
            </Field>
            <Field label={t.maintenance.createdAt}>
              {formatDate(maintenancePlan.createdAt)}
            </Field>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-lg font-bold m-0">
              {t.maintenance.nextActionTitle}
            </h2>
            {schedule?.kind === "no-interval" ? (
              <p className="text-gray-500 m-0">
                {t.maintenance.nextActionNoInterval}
              </p>
            ) : schedule?.kind === "scheduled" ? (
              <div className="flex flex-col gap-2">
                <Field label={t.maintenance.nextActionDue}>
                  {formatYearMonth(schedule.nextActionDate)}
                </Field>
                {schedule.actionInCurrentYear && (
                  <p className="m-0 text-green-700">
                    {t.maintenance.actionInCurrentYear}
                  </p>
                )}
                {schedule.nextActionOverdue && (
                  <p className="m-0 text-amber-700 font-medium">
                    {t.maintenance.nextActionOverdue}
                  </p>
                )}
              </div>
            ) : null}
          </div>

          <div className="flex flex-col gap-4">
            <h2 className="text-lg font-bold m-0">{t.maintenance.actions}</h2>
            <div className="flex flex-wrap items-end gap-3">
              <Select
                className="min-w-[7rem]"
                label={t.maintenance.actionYear}
                options={yearOptions}
                value={selectedYear}
                onChange={setSelectedYear}
              />
              <Select
                className="min-w-[10rem]"
                label={t.maintenance.actionMonth}
                options={monthOptions}
                value={selectedMonth}
                onChange={setSelectedMonth}
              />
              <Button
                loading={createAction.isPending}
                type="button"
                onClick={() => void handleAddAction()}
              >
                {t.maintenance.actionAdd}
              </Button>
            </div>
            {actions && actions.length > 0 ? (
              <ul className="flex flex-col gap-3 list-none m-0 p-0">
                {actions.map((action) => (
                  <MaintenancePlanActionRow
                    key={action.id}
                    action={action}
                    maintenancePlanId={id}
                  />
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 m-0">{t.maintenance.actionsEmpty}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              route={`/admin/maintenance/plan/${id}/edit`}
              severity="secondary"
            >
              {t.page.edit}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function MaintenancePlanActionRow({
  action,
  maintenancePlanId,
}: {
  action: MaintenancePlanAction;
  maintenancePlanId: string;
}) {
  const { locale: t, get: getLocale } = useLocale();
  const deleteAction = useDeleteMaintenancePlanAction(maintenancePlanId);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 border border-gray-200 rounded-xs p-3">
      <span>{formatYearMonth(new Date(action.date))}</span>
      <Button
        severity="danger"
        size="sm"
        type="button"
        onClick={() => setDeleteConfirmOpen(true)}
      >
        {t.maintenance.deleteAction}
      </Button>
      {deleteConfirmOpen && (
        <Confirm
          header={getLocale("maintenance.confirmDeleteAction")}
          loading={deleteAction.isPending}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            void (async () => {
              await deleteAction.mutateAsync(action.id);
              setDeleteConfirmOpen(false);
            })();
          }}
        />
      )}
    </li>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <span className="font-bold shrink-0">{label}:</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
